import { z } from 'zod';

// Staff, as Lagos Life's 🧹 Staff (read from its app on 21 October 2026): help for the house, hired from the
// phone. The first part-week is paid when they start, then wages come off every Saturday morning with the rent.
// Each role does one thing for you while you are at home; some need a bigger home (a real kitchen, a gate to
// guard), a car, or a baby, and have to go when you no longer have it. Wages already paid are not given back.
// All of it is content the admins edit.

const id = z.string().regex(/^[a-z0-9_]{2,30}$/);
export const STAFF_NEEDS = ['nothing', 'home', 'car', 'baby'] as const;
export type StaffNeed = (typeof STAFF_NEEDS)[number];
export const StaffRole = z.object({
  id,
  label: z.string().trim().min(2).max(40),
  // the person's name, as Lagos names them (Mama Bisi, Chef Tolu)
  name: z.string().trim().min(2).max(40),
  emoji: z.string().trim().min(1).max(16),
  blurb: z.string().trim().min(10).max(200),
  // what they do for you, in a line
  perk: z.string().trim().min(10).max(200),
  wagePenceWeekly: z.number().int().min(100).max(100_000_000),
  // what hiring needs: a home of at least this tier (houses.ts), a car, a baby; or nothing
  needs: z.enum(STAFF_NEEDS),
  minTier: z.number().int().min(1).max(4),
  livesIn: z.boolean(),
  visible: z.boolean(),
});
export type StaffRole = z.infer<typeof StaffRole>;

export const StaffContent = z.object({
  visible: z.boolean(),
  // payday: the weekday (Monday 0 … Sunday 6) and London hour wages come off
  payWeekday: z.number().int().min(0).max(6),
  payHour: z.number().int().min(0).max(23),
  // what the roles do in the game: after a sleep at home the cleaner has hygiene up to at least this (fresh
  // clothes every morning); the chef makes cooking at home free and fill this much more; the guard makes a
  // sleep at home restore this much more energy
  cleanerHygiene: z.number().int().min(0).max(100),
  chefHungerBonus: z.number().int().min(0).max(100),
  guardEnergyBonus: z.number().int().min(0).max(100),
  roles: z.array(StaffRole).min(1).max(20),
});
export type StaffContent = z.infer<typeof StaffContent>;

const role = (value: Omit<StaffRole, 'visible'>): StaffRole => ({ ...value, visible: true });
export const DEFAULT_STAFF: StaffContent = {
  visible: true,
  payWeekday: 5,
  payHour: 9,
  cleanerHygiene: 85,
  chefHungerBonus: 25,
  guardEnergyBonus: 10,
  roles: [
    role({
      id: 'cleaner',
      label: 'Cleaner',
      name: 'Grace',
      emoji: '🧹',
      blurb: 'Comes in every morning: hoovers, mops, does the washing and irons your clothes.',
      perk: 'A spotless home and fresh clothes every morning you wake up at home',
      wagePenceWeekly: 12_000,
      needs: 'nothing',
      minTier: 1,
      livesIn: false,
    }),
    role({
      id: 'chef',
      label: 'Private chef',
      name: 'Chef Marcus',
      emoji: '👨🏾‍🍳',
      blurb: 'Shops at Borough Market and cooks proper food: Sunday roast, jollof, a Thai green curry, you name it.',
      perk: 'Cooking at home costs nothing and fills you up more',
      wagePenceWeekly: 45_000,
      needs: 'home',
      minTier: 3,
      livesIn: false,
    }),
    role({
      id: 'driver',
      label: 'Driver',
      name: 'Dev',
      emoji: '🧑🏾‍✈️',
      blurb: 'Knows every rat run from Canary Wharf to Croydon. Pays the congestion charge, finds the parking.',
      perk: 'Your car becomes "Driver" on the travel sheet: no fuel to pay and a quicker trip',
      wagePenceWeekly: 36_000,
      needs: 'car',
      minTier: 1,
      livesIn: false,
    }),
    role({
      id: 'guard',
      label: 'Security guard',
      name: 'Musa',
      emoji: '💂🏾',
      blurb: 'SIA-licensed. Mans the door day and night and walks behind you when you step out.',
      perk: 'You sleep easy: a night at home restores more energy',
      wagePenceWeekly: 30_000,
      needs: 'home',
      minTier: 3,
      livesIn: false,
    }),
    role({
      id: 'nanny',
      label: 'Nanny',
      name: 'Aunty Joy',
      emoji: '👩🏾‍🍼',
      blurb: 'Lives in, loves your children like her own, and sings them to sleep.',
      perk: 'Feeds and plays with your babies whenever you open Family',
      wagePenceWeekly: 40_000,
      needs: 'baby',
      minTier: 1,
      livesIn: true,
    }),
  ],
};
export const staffOf = (content: { staff?: StaffContent }) => content.staff ?? DEFAULT_STAFF;
export function staffProblems(staff: StaffContent): string[] {
  const ids = new Set(staff.roles.map((item) => item.id));
  return ids.size === staff.roles.length ? [] : ['Staff: two roles have the same id.'];
}

// the first part-week: wages for the days until payday, by the day, at least one day
export function firstWeekPence(wagePenceWeekly: number, hiredAt: Date, payday: Date) {
  const days = Math.max(1, Math.min(7, Math.ceil((payday.getTime() - hiredAt.getTime()) / 86_400_000)));
  return Math.round((wagePenceWeekly * days) / 7);
}
// what stops a hire now (or makes staff leave): the words the player sees
export function staffBlock(
  role: StaffRole,
  have: { homeTier: number; car: boolean; baby: boolean },
  homeNeeded: string,
): string | null {
  if (role.needs === 'home' && have.homeTier < role.minTier) return `Needs a bigger home (${homeNeeded} or bigger)`;
  if (role.needs === 'car' && !have.car) return 'You need a car first';
  if (role.needs === 'baby' && !have.baby) return 'For when you have a baby';
  return null;
}

// ---------- over the wire ----------

export const StaffMemberView = z.object({
  id: z.string().uuid(),
  role: z.string(),
  hiredAt: z.string(),
  // the week paid up to, and what the next week costs
  paidTo: z.string(),
  wagePenceWeekly: z.number().int(),
});
export type StaffMemberView = z.infer<typeof StaffMemberView>;
export const StaffOfferView = z.object({
  role: z.string(),
  // what hiring now costs (the first part-week), and why it cannot be hired (null when it can)
  firstPence: z.number().int(),
  blocked: z.string().nullable(),
});
export type StaffOfferView = z.infer<typeof StaffOfferView>;
export const StaffReply = z.object({
  staff: z.array(StaffMemberView),
  offers: z.array(StaffOfferView),
  // the next payday
  paydayAt: z.string(),
  weeklyPence: z.number().int(),
  walletPence: z.number().int(),
  message: z.string().optional(),
});
export type StaffReply = z.infer<typeof StaffReply>;
export const HireStaffRequest = z.object({ role: id }).strict();
export type HireStaffRequest = z.infer<typeof HireStaffRequest>;
